import React from 'react';
import PropTypes from 'prop-types';

import { idToLink } from 'inside-out/managers/navManager';
import { extractAudiences } from 'inside-out/managers/resourceLibraryManager';

import { Cards } from 'inside-out/components/shared/cards';
import { ResourceCard } from 'inside-out/components/shared/cards/resourceCard';
import { InPagePrompt } from 'inside-out/components/shared/inPagePrompt';
import { Pagination } from 'inside-out/components/shared/pagination';

import {
  sectionStyle,
  pageControlsStyle,
  subTitleStyle,
} from 'inside-out/page-styles/resource-library';


const NodeCard = ({ node }) => (
  <ResourceCard
    id={node.id}
    to={idToLink(node.id)}
    background={node.fields && node.fields.thumbnail_image.childImageSharp.resize.src}
    audiences={extractAudiences(node.categories)}
    hasVideo={!!node.attachments.find(a => a.vimeo_url)}
    title={node.title}
    body={node.extract}
    date={node.date}/>
);

export const ResultsBlock = ({ data, page, filters, onPageChange }) => {
  const { totalResources, totalPages, nodes } = data;
  const pageSettings = page.edges[0].node;
  const isEmpty = totalResources < 1;
  return (
    <React.Fragment>
      <div className={pageControlsStyle}>
        {!isEmpty && <p>{totalResources} resource{totalResources > 1 ? 's' : ''}</p>}
        {isEmpty && <p>0 results</p>}
      </div>

      {isEmpty && (
        <section className={sectionStyle}>
          <header>
            <h1 className={subTitleStyle}>
              There are currently no resources within this selection.
            </h1>
          </header>
          <p>Try changing your search criteria, or view all resources.</p>
        </section>
      )}

      {!isEmpty && (
        <section className={sectionStyle}>
          <Cards>
            {nodes.slice(0, 9)
              .map(node => <NodeCard node={node} key={node.id}/>)
            }
          </Cards>
        </section>
      )}

      {!isEmpty && (
        <InPagePrompt
          topText={pageSettings.purple_heading}
          bottomText={pageSettings.purple_body}
          cta={{
            to: pageSettings.button_to,
            text: pageSettings.button_text,
          }}
          inline={true}/>
      )}

      {!isEmpty && (
        <section className={sectionStyle}>
          <Cards>
            {nodes.slice(9)
              .map(node => <NodeCard node={node} key={node.id}/>)
            }
          </Cards>
        </section>
      )}

      {totalPages > 1 && (
        <Pagination
          initialPage={filters.page}
          pageCount={totalPages}
          onPageChange={page => onPageChange(page)}/>
      )}
    </React.Fragment>
  );
};


const nodePropType = PropTypes.shape({
  id: PropTypes.string.isRequired,
  title: PropTypes.string.isRequired,
  extract: PropTypes.string,
  date: PropTypes.string,
  categories: PropTypes.object,
  fields: PropTypes.shape({
    thumbnail_image: PropTypes.shape({
      childImageSharp: PropTypes.shape({
        resize: PropTypes.shape({
          src: PropTypes.string.isRequired,
        }).isRequired,
      }).isRequired,
    }).isRequired,
  }),
  attachments: PropTypes.arrayOf(
    PropTypes.shape({
      vimeo_url: PropTypes.string,
    })
  ),
});

NodeCard.propTypes = {
  node: nodePropType,
};

ResultsBlock.propTypes = {
  data: PropTypes.shape({
    totalResources: PropTypes.number.isRequired,
    totalPages: PropTypes.number.isRequired,
    nodes: PropTypes.arrayOf(nodePropType),
  }),
  page: PropTypes.shape({
    edges: PropTypes.arrayOf(
      PropTypes.shape({
        node: PropTypes.shape({
          purple_heading: PropTypes.string.isRequired,
          purple_body: PropTypes.string.isRequired,
          button_text: PropTypes.string.isRequired,
          button_to: PropTypes.string.isRequired,
        }).isRequired
      })
    ).isRequired
  }).isRequired,
  filters: PropTypes.object,
  onPageChange: PropTypes.func.isRequired,
};



// WEBPACK FOOTER //
// ./src/components/resource-library/resultsBlock/component.js